/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * Builds the `snapshot.insights` section for the insight widgets (finance, bank,
 * sales, operations, custom). Data comes from `GET /api/widgets/insights`; the
 * app resolves every user-visible string into `labels` (contract C7 — native
 * widgets cannot read the JS i18n catalog).
 *
 * Shape (iOS reads it verbatim):
 *   { generatedAt, restaurantId, tz, locale, periods, extras,
 *     presets: { [KindId]: { ...preset, config } },
 *     catalog: { kpis, series, breakdowns, lists },
 *     labels: { kpi, series, breakdown, list, item, period, periodShort, module,
 *               option, common, title, description, group } }
 */

import { apiFetch } from '../WorkSheetContext';
import {
    INSIGHT_WIDGETS,
    INSIGHT_PERIODS,
    INSIGHT_MODULES,
    buildInsightCatalog,
    humanizeKey,
    insightModulesFor,
    resolveInsightConfig,
    toSnapshotPreset,
    MAX_INSIGHT_PERIODS,
    unionInsightPeriods,
} from '../../config/insightWidgets';
import huInsights from '../../../locales/insights/hu.json';

/** Extras the backend can return (`parseInsightsQuery`). */
const ALL_EXTRAS = ['topProducts', 'bank'];

/**
 * Value that makes the backend return NO extras. An empty `extras=` falls back to
 * "both" (`rawExtras.length > 0 ? extras : ALLOWED`), whereas a non-empty value with
 * no valid entry is filtered down to an empty list.
 */
const NO_EXTRAS = 'none';

/**
 * Lazy, guarded i18n translator (background tasks may run before init).
 * @returns {{ t: Function, language: string }|null}
 */
const getTranslator = () => {
    try {
        // eslint-disable-next-line global-require
        return require('../../../i18n').default;
    } catch (_error) {
        return null;
    }
};

/**
 * Resolve a flat catalog key with a fallback (never blank, never the raw key).
 * @param {object|null} translator i18n instance.
 * @param {string} key Catalog key.
 * @param {string} fallback Fallback text.
 * @returns {string}
 */
const translate = (translator, key, fallback) => {
    try {
        const value = translator?.t?.(key, { defaultValue: fallback });
        if (typeof value === 'string' && value.trim() && value !== key) {
            return value;
        }
    } catch (_error) {
        // fall through to the fallback
    }

    return fallback;
};

/**
 * Collect the keys of the Hungarian catalog under a prefix.
 * @param {string} prefix e.g. `wi_item_`
 * @returns {string[]} Remainders.
 */
const keysWithPrefix = (prefix) => Object.keys(huInsights)
    .filter((key) => key.startsWith(prefix))
    .map((key) => key.slice(prefix.length));

/**
 * Resolve every label the widgets need.
 * @param {object} payload Insights payload (for item keys not in the catalog).
 * @param {object} catalog Built catalog.
 * @param {object|null} translator i18n instance.
 * @returns {object} `labels` block.
 */
export const buildInsightLabels = (payload, catalog, translator) => {
    const labels = {
        kpi: {}, series: {}, breakdown: {}, list: {}, item: {}, period: {}, periodShort: {}, module: {},
        option: {}, common: {}, title: {}, description: {}, group: {},
    };
    const t = (key, fallback) => translate(translator, key, fallback);

    [['kpi', 'kpis'], ['series', 'series'], ['breakdown', 'breakdowns'], ['list', 'lists']].forEach(([kind, bucket]) => {
        keysWithPrefix(`wi_${kind}_`).forEach((rest) => {
            const cut = rest.indexOf('_');
            const module = rest.slice(0, cut);
            const key = rest.slice(cut + 1);
            labels[kind][`${module}.${key}`] = t(`wi_${kind}_${rest}`, humanizeKey(key));
        });
        (catalog?.[bucket] || []).forEach((entry) => {
            if (!labels[kind][entry.id]) {
                labels[kind][entry.id] = t(`wi_${kind}_${entry.module}_${entry.id.split('.')[1]}`, entry.label);
            }
        });
    });

    keysWithPrefix('wi_item_').forEach((key) => {
        labels.item[key] = t(`wi_item_${key}`, humanizeKey(key));
    });
    // Breakdown item keys the catalog does not know yet: fall back to the backend label / humanized key.
    Object.keys(payload?.periods || {}).forEach((period) => {
        Object.values(payload.periods[period]?.modules || {}).forEach((block) => {
            Object.values(block?.breakdowns || {}).forEach((breakdown) => {
                (breakdown?.items || []).forEach((item) => {
                    if (item?.key && !labels.item[item.key]) {
                        labels.item[item.key] = t(`wi_item_${item.key}`, String(item.label || humanizeKey(item.key)));
                    }
                });
            });
        });
    });

    INSIGHT_PERIODS.forEach((period) => {
        labels.period[period] = t(`wi_period_${period}`, period);
        labels.periodShort[period] = t(`wi_periodshort_${period}`, period);
    });
    INSIGHT_MODULES.forEach((module) => {
        labels.module[module] = t(`wi_module_${module}`, humanizeKey(module));
    });
    keysWithPrefix('wi_option_').forEach((name) => {
        labels.option[name] = t(`wi_option_${name}`, humanizeKey(name));
    });
    keysWithPrefix('wi_opt_').forEach((rest) => {
        const [name, ...value] = rest.split('_');
        labels.option[`${name}.${value.join('_')}`] = t(`wi_opt_${rest}`, humanizeKey(value.join('_')));
    });
    keysWithPrefix('wi_common_').forEach((key) => {
        labels.common[key] = t(`wi_common_${key}`, humanizeKey(key));
    });
    INSIGHT_WIDGETS.forEach((preset) => {
        labels.title[preset.id] = t(preset.titleKey, humanizeKey(preset.id));
        labels.description[preset.id] = t(preset.descriptionKey, '');
    });
    ['finance', 'sales', 'operations', 'custom'].forEach((group) => {
        labels.group[group] = t(`wi_group_${group}`, humanizeKey(group));
    });

    return labels;
};

/** Preset `extra` values mapped to the backend extra they need. */
const EXTRA_FOR_PRESET = { topProducts: 'topProducts', bank: 'bank', bankAccounts: 'bank' };

/**
 * Periods, modules and extras to request. Votes come from, in priority order:
 *  1. every placed Android instance (counted first; never evicted),
 *  2. app-level prefs ONLY for kinds the user explicitly configured,
 *  3. a `today` + `mtd` fallback when nothing at all is configured.
 * Capped at 4 periods and always including `today`. Empty `modules` means "let the
 * backend use its defaults" (nothing configured).
 * @param {Record<string, object>} appPrefs `prefs.insights`.
 * @param {Array<{ kind: string, config: object }>} instances Android instance entries.
 * @returns {{ periods: string[], modules: string[], extras: string[], configs: Record<string, object> }}
 */
export const planInsightFetch = (appPrefs, instances = []) => {
    const configs = {};
    INSIGHT_WIDGETS.forEach((preset) => {
        configs[preset.id] = resolveInsightConfig(preset.id, appPrefs?.[preset.id]);
    });
    const instanceLists = [];
    const prefLists = [];
    const modules = new Set();
    const extras = new Set();
    const collect = (preset, resolved, lists) => {
        lists.push([resolved.period]);
        insightModulesFor(preset, resolved).forEach((m) => modules.add(m));
        if (EXTRA_FOR_PRESET[preset.extra]) {
            extras.add(EXTRA_FOR_PRESET[preset.extra]);
        }
    };
    (Array.isArray(instances) ? instances : []).forEach((entry) => {
        const preset = INSIGHT_WIDGETS.find((item) => item.id === entry?.kind);
        if (preset) {
            collect(preset, resolveInsightConfig(preset.id, appPrefs?.[preset.id], entry.config), instanceLists);
        }
    });
    INSIGHT_WIDGETS.forEach((preset) => {
        const explicit = appPrefs?.[preset.id];
        if (explicit && typeof explicit === 'object') {
            collect(preset, configs[preset.id], prefLists);
        }
    });
    if (instanceLists.length === 0 && prefLists.length === 0) {
        prefLists.push(['mtd']);
    }
    // Instance periods first (they can never be evicted), then pref/fallback ones.
    const ranked = (lists) => unionInsightPeriods(lists, INSIGHT_PERIODS.length + 1).filter((period) => period !== 'today');
    const fromInstances = ranked(instanceLists);
    const fromPrefs = ranked(prefLists).filter((period) => !fromInstances.includes(period));
    const periods = ['today', ...fromInstances, ...fromPrefs].slice(0, MAX_INSIGHT_PERIODS);

    return { periods, modules: Array.from(modules), extras: ALL_EXTRAS.filter((e) => extras.has(e)), configs };
};

/**
 * Drop extras that were not requested (privacy: bank counterparties must not linger
 * in storage once no widget needs them).
 * @param {object|null|undefined} extras Payload extras.
 * @param {string[]} requested Requested extras.
 * @returns {object} Pruned copy.
 */
export const pruneInsightExtras = (extras, requested) => {
    const out = {};
    ALL_EXTRAS.forEach((name) => {
        if (requested.includes(name) && extras?.[name] !== undefined) {
            out[name] = extras[name];
        }
    });

    return out;
};

/**
 * Per period + module stale guard: a fresh `timeout`/`error` block never overwrites a
 * prior `ok` block of the same period+module. `forbidden` ALWAYS overwrites (revoked
 * access must hide data).
 * @param {object} freshPeriods `fresh.periods`.
 * @param {object|null} priorPeriods `prior.periods` (same restaurant) or null.
 * @returns {object} Merged periods.
 */
export const mergeInsightPeriods = (freshPeriods, priorPeriods) => {
    if (!priorPeriods) {
        return freshPeriods;
    }
    const merged = {};
    Object.keys(freshPeriods || {}).forEach((period) => {
        const freshModules = freshPeriods[period]?.modules || {};
        const priorModules = priorPeriods[period]?.modules || {};
        const modules = { ...freshModules };
        Object.keys(freshModules).forEach((module) => {
            const status = freshModules[module]?.status;
            if ((status === 'timeout' || status === 'error') && priorModules[module]?.status === 'ok') {
                modules[module] = priorModules[module];
            }
        });
        merged[period] = { ...freshPeriods[period], modules };
    });

    return merged;
};

/**
 * Fetch the insights payload. Returns `null` on any failure / malformed body.
 * `x-restaurant-id` + `skipDedup` are mandatory (path-keyed dedupe would
 * otherwise merge differently scoped requests).
 * @param {string|null} restaurantId
 * @param {string[]} periods
 * @param {string[]} modules Empty → backend default modules.
 * @param {string[]} [extras] Empty → `extras=none`.
 * @returns {Promise<object|null>}
 */
export const fetchInsights = async (restaurantId, periods, modules, extras = []) => {
    try {
        const parts = [`periods=${periods.join(',')}`];
        if (modules.length > 0) {
            parts.push(`modules=${modules.join(',')}`);
        }
        parts.push(`extras=${extras.length > 0 ? extras.join(',') : NO_EXTRAS}`);
        const query = parts.join('&');
        const res = await apiFetch(`/widgets/insights?${query}`, {
            headers: restaurantId ? { 'x-restaurant-id': String(restaurantId) } : {},
            skipDedup: true,
        });

        return res && typeof res === 'object' && res.periods && typeof res.periods === 'object' ? res : null;
    } catch (_error) {
        return null;
    }
};

/**
 * Build the `insights` snapshot section. Never throws; on a failed fetch the
 * prior snapshot's data is carried over (stale-guard) while presets/labels are
 * always refreshed so config changes apply immediately.
 * @param {{ restaurantId: string|null, prefs: object, instances: object[], prior: object|null }} args
 * @returns {Promise<object>}
 */
export const buildInsightsSection = async ({ restaurantId, prefs, instances, prior }) => {
    const plan = planInsightFetch(prefs?.insights, instances);
    const fresh = await fetchInsights(restaurantId, plan.periods, plan.modules, plan.extras);
    const priorInsights = prior?.insights && prior.insights.restaurantId === (restaurantId ? String(restaurantId) : prior.insights.restaurantId)
        ? prior.insights
        : null;
    const source = fresh || priorInsights;
    const translator = getTranslator();
    const periods = fresh ? mergeInsightPeriods(fresh.periods, priorInsights?.periods) : (source?.periods || {});
    const payload = { periods, extras: pruneInsightExtras(source?.extras, plan.extras) };
    const catalog = fresh
        ? buildInsightCatalog(payload, (kind, module, key) => translate(translator, `wi_${kind}_${module}_${key}`, humanizeKey(key)))
        : (priorInsights?.catalog || { kpis: [], series: [], breakdowns: [], lists: [] });
    const presets = {};
    INSIGHT_WIDGETS.forEach((preset) => {
        presets[preset.id] = toSnapshotPreset(preset, plan.configs[preset.id]);
    });

    return {
        generatedAt: fresh ? (fresh.generatedAt || new Date().toISOString()) : (source?.generatedAt || null),
        restaurantId: restaurantId ? String(restaurantId) : (source?.restaurantId || null),
        tz: source?.tz || null,
        locale: translator?.language || 'hu',
        periods: payload.periods,
        extras: payload.extras,
        presets,
        catalog,
        labels: buildInsightLabels(payload, catalog, translator),
    };
};
